import React, { useState, useEffect, useMemo } from 'react';
import { useAuction } from '../../context/AuctionContext';
import { useAuth } from '../../context/AuthContext';
import { collection, onSnapshot, query, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { AUCTION_COMPETITION_PHASES } from '../../constants/game';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    FormControl,
    Select,
    MenuItem,
    Tooltip,
    useMediaQuery
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTheme } from '@mui/material/styles';
import ParticipantTeamModal from '../../components/ParticipantTeamModal';
import { containers, page, table } from '../../theme/commonStyles';
import { fetchGoldMedalUids } from '../../services/history';

// Goud bekertje achter de naam van een oud-kampioen (won ooit een seizoen).
const GoldCup = ({ show }) => (show ? (
    <Tooltip title="Winnaar van minstens één seizoen">
        <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 16, verticalAlign: 'text-bottom', ml: 0.5 }} />
    </Tooltip>
) : null);

const RankingTable = ({ ranking, onRowClick, isMobile, currentUserId, showChampion, showMedals, medalCounts, goldUids }) => (
    <Box sx={containers.glassContainer}>
        <TableContainer component={Paper}>
            <Table aria-label="ranking table">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '30px', p: 1 }}>#</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell align="right">PNT</TableCell>
                        <TableCell align="right">Score</TableCell>
                        {!isMobile && (
                            <>
                                <TableCell align="right">W</TableCell>
                                <TableCell align="right">G</TableCell>
                                <TableCell align="right">V</TableCell>
                            </>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ranking.map((row, index) => {
                        const isCurrentUser = row.uid === currentUserId;
                        const isChampion = showChampion && index === 0;
                        const medals = showMedals ? (medalCounts?.[row.uid] || 0) : 0;
                        const hasGoldCup = goldUids?.has(row.uid);
                        return (
                            <TableRow
                                key={row.uid}
                                hover
                                onClick={() => onRowClick(row)}
                                sx={{
                                    ...table.clickableCell,
                                    ...(isCurrentUser && table.highlightedRow),
                                }}
                            >
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                    {index + 1}
                                </TableCell>
                                <TableCell sx={isChampion ? { color: '#fecb00', fontWeight: 'bold' } : undefined}>
                                    {row.name}{isChampion ? ' 🏆' : ''}{medals > 0 ? ' ' + '🥇'.repeat(medals) : ''}
                                    <GoldCup show={hasGoldCup} />
                                </TableCell>
                                <TableCell align="right" sx={table.primaryBoldCell}>
                                    {row.matchPoints || 0}
                                </TableCell>
                                <TableCell align="right">
                                    {(row.playerPoints || 0) + (row.tacticPoints || 0) + (row.tacticCounter || 0)}
                                </TableCell>
                                {!isMobile && (
                                    <>
                                        <TableCell align="right">{row.wins || 0}</TableCell>
                                        <TableCell align="right">{row.draws || 0}</TableCell>
                                        <TableCell align="right">{row.losses || 0}</TableCell>
                                    </>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

/**
 * Ranglijst van de veiling-competitie (het subspel tijdens de Veiling-fase).
 *
 * Kolommen: # / Team / Gescoute spelers over (O) / Gescoute spelers binnen (B) /
 * Uitgegeven aan de gescoute spelers (€). De rijen zijn al server-side gesorteerd.
 */
const AuctionCompetitionTable = ({ ranking, onRowClick, currentUserId, goldUids }) => (
    <Box sx={containers.glassContainer}>
        <TableContainer component={Paper}>
            <Table aria-label="auction competition ranking table">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '30px', p: 1 }}>#</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell align="right">O</TableCell>
                        <TableCell align="right">B</TableCell>
                        <TableCell align="right">€</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ranking.map((row, index) => {
                        const isCurrentUser = row.uid === currentUserId;
                        return (
                            <TableRow
                                key={row.uid}
                                hover
                                onClick={() => onRowClick(row)}
                                sx={{
                                    ...table.clickableCell,
                                    ...(isCurrentUser && table.highlightedRow),
                                }}
                            >
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                    {index + 1}
                                </TableCell>
                                <TableCell>{row.name}<GoldCup show={goldUids?.has(row.uid)} /></TableCell>
                                <TableCell align="right">{row.over || 0}</TableCell>
                                <TableCell align="right" sx={table.primaryBoldCell}>{row.binnen || 0}</TableCell>
                                <TableCell align="right">{row.spent || 0}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

function Rankings() {
    const { user } = useAuth();
    const { auctionId, auction: contextAuction } = useAuction();
    const effectivePhase = contextAuction?.spelfase;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [rankingData, setRankingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [participants, setParticipants] = useState([]); // Still needed for the modal
    const [closedRounds, setClosedRounds] = useState([]);
    const [selectedRound, setSelectedRound] = useState('total');
    const [auction, setAuction] = useState(null);
    const [modalRoundId, setModalRoundId] = useState(null);
    const [finalRankingPoule, setFinalRankingPoule] = useState(null);
    const [liveRankingIntermediateRound, setLiveRankingIntermediateRound] = useState(null);
    const [liveRankingFinaleronde, setLiveRankingFinaleronde] = useState(null);
    const [finalRoundParticipants, setFinalRoundParticipants] = useState(null);
    const [knockoutRounds, setKnockoutRounds] = useState([]);
    const [finalRoundSchedule, setFinalRoundSchedule] = useState([]);
    const [achtsteFinaleRounds, setAchtsteFinaleRounds] = useState([]);
    const [pouleWinCounts, setPouleWinCounts] = useState({});
    const [goldUids, setGoldUids] = useState(new Set());
    const [roundWinners, setRoundWinners] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [auctionCompetition, setAuctionCompetition] = useState([]);

    // Helper om tussenronde knockout rondes te herkennen
    const isIntermediateRound = (roundData) => {
        return roundData.id?.startsWith('intermediateRound') || 
            (roundData.type === 'knockout' && roundData.stageName === 'Knockout Round Play-offs');
    };

    // Helper om finaleronde rondes te herkennen
    const isFinalRound = (roundData) => {
        return roundData.type === 'knockout' && ['Quarter-finals', 'Semi-finals', 'Final'].includes(roundData.stageName);
    };

    // Helper om achtste finale rondes te herkennen
    const isAchtsteFinaleRound = (roundData) => {
        return roundData.type === 'knockout' && roundData.stageName === '8th Finals';
    };

    // Helper om ALLE knockout rondes te herkennen
    const isAnyKnockoutRound = (roundData) => {
        return roundData.type === 'knockout' || roundData.id?.startsWith('intermediateRound');
    };

    useEffect(() => {
        if (!auctionId) {
            setLoading(false);
            return;
        }

        // Fetch auction data to get seasonId
        const unsubAuction = onSnapshot(doc(db, 'auctions', auctionId), (docSnap) => {
            if (docSnap.exists()) {
                setAuction(docSnap.data());
            }
        }, (err) => {
            console.error("Error fetching auction data:", err);
        });

        // We still need participants data to pass the full participant object to the modal
        const participantsQuery = query(collection(db, 'auctions', auctionId, 'participants'));
        const unsubscribeParticipants = onSnapshot(participantsQuery, (snapshot) => {
            setParticipants(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.error("Error fetching participants:", err);
        });

        const scheduleQuery = query(collection(db, 'auctions', auctionId, 'schedule'));
        const unsubscribeSchedule = onSnapshot(scheduleQuery, (snapshot) => {
            const allRounds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Filter gesloten reguliere rondes (niet knockout)
            const regularRounds = allRounds.filter(round => 
                round.status === 'closed' && !isAnyKnockoutRound(round)
            );
            setClosedRounds(regularRounds);
            
            // Filter tussenronde rondes (Knockout Round Play-offs)
            const knockouts = allRounds
                .filter(round => isIntermediateRound(round))
                .sort((a, b) => (a.leg || '').localeCompare(b.leg || ''));
            setKnockoutRounds(knockouts);

            // Filter achtste finale rondes
            const achtste = allRounds
                .filter(round => isAchtsteFinaleRound(round))
                .sort((a, b) => (a.leg || '').localeCompare(b.leg || ''));
            setAchtsteFinaleRounds(achtste);

            // Filter finaleronde rondes (Quarter-finals, Semi-finals, Final)
            const finals = allRounds
                .filter(round => isFinalRound(round))
                .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
            setFinalRoundSchedule(finals);
        }, (err) => {
            console.error("Error fetching schedule:", err);
        });

        // Listener voor finalRankingPoule
        const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
        const unsubFinalRankingPoule = onSnapshot(finalRankingPouleRef, (docSnap) => {
            if (docSnap.exists()) {
                setFinalRankingPoule(docSnap.data());
            } else {
                setFinalRankingPoule(null);
            }
        }, (err) => {
            console.error("Error fetching finalRankingPoule:", err);
        });

        // Listener voor liveRankingIntermediateRound
        const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
        const unsubLiveRankingIntermediate = onSnapshot(liveRankingIntermediateRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveRankingIntermediateRound(docSnap.data());
            } else {
                setLiveRankingIntermediateRound(null);
            }
        }, (err) => {
            console.error("Error fetching liveRankingIntermediateRound:", err);
        });

        const liveRankingFinalRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde');
        const unsubLiveRankingFinal = onSnapshot(liveRankingFinalRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveRankingFinaleronde(docSnap.data());
            } else {
                setLiveRankingFinaleronde(null);
            }
        }, (err) => {
            console.error("Error fetching liveRankingFinaleronde:", err);
        });

        const finalRoundParticipantsRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants');
        const unsubFinalRoundParticipants = onSnapshot(finalRoundParticipantsRef, (docSnap) => {
            if (docSnap.exists()) {
                setFinalRoundParticipants(docSnap.data());
            } else {
                setFinalRoundParticipants(null);
            }
        }, (err) => {
            console.error("Error fetching finalRoundParticipants:", err);
        });

        // Listener voor auctionTransfers (voor spelersdeelnemingen)
        const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'));
        const unsubTransfers = onSnapshot(transfersQuery, (snapshot) => {
            setTransfers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.error("Error fetching auctionTransfers:", err);
        });

        // Listener voor de veiling-competitie (subspel tijdens de Veiling-fase)
        const auctionCompetitionRef = doc(db, 'auctions', auctionId, 'ranking', 'auctionCompetition');
        const unsubAuctionCompetition = onSnapshot(auctionCompetitionRef, (docSnap) => {
            setAuctionCompetition(docSnap.exists() ? (docSnap.data().ranking || []) : []);
        }, (err) => {
            console.error("Error fetching auctionCompetition ranking:", err);
        });


        return () => {
            unsubAuction();
            unsubscribeParticipants();
            unsubscribeSchedule();
            unsubFinalRankingPoule();
            unsubLiveRankingIntermediate();
            unsubLiveRankingFinal();
            unsubFinalRoundParticipants();
            unsubTransfers();
            unsubAuctionCompetition();
        };
    }, [auctionId]);

    useEffect(() => {
        if (!auctionId) {
            setLoading(false);
            return;
        }
        setLoading(true);

        let pathSegments;
        if (selectedRound === 'total') {
            pathSegments = ['auctions', auctionId, 'ranking', 'liveRanking'];
        } else if (selectedRound === 'finalRankingPoule') {
            pathSegments = ['auctions', auctionId, 'ranking', 'finalRankingPoule'];
        } else if (selectedRound === 'liveRankingIntermediateRound') {
            pathSegments = ['auctions', auctionId, 'ranking', 'liveRankingIntermediateRound'];
        } else if (selectedRound === 'liveRankingFinaleronde') {
            pathSegments = ['auctions', auctionId, 'ranking', 'liveRankingFinaleronde'];
        } else {
            pathSegments = ['auctions', auctionId, 'roundRankings', selectedRound];
        }

        const finalPath = pathSegments.join('/');
        const rankingRef = doc(db, finalPath);

        const unsubscribeRanking = onSnapshot(rankingRef, (docSnap) => {
            if (docSnap.exists()) {
                let data;
                if (selectedRound === 'total' || selectedRound === 'finalRankingPoule' || selectedRound === 'liveRankingIntermediateRound' || selectedRound === 'liveRankingFinaleronde') {
                    data = docSnap.data().ranking || [];
                    if (selectedRound === 'liveRankingFinaleronde' && finalRoundParticipants?.participantUids?.length) {
                        const finalistSet = new Set(finalRoundParticipants.participantUids);
                        data = data.filter(row => finalistSet.has(row.uid));
                    }
                } else {
                    const roundData = docSnap.data();
                    data = (roundData.results || []).sort((a, b) => {
                        if (b.matchPoints !== a.matchPoints) {
                            return b.matchPoints - a.matchPoints;
                        }
                        const aScorePoints = (a.playerPoints || 0) + (a.tacticPoints || 0) + (a.tacticCounter || 0);
                        const bScorePoints = (b.playerPoints || 0) + (b.tacticPoints || 0) + (b.tacticCounter || 0);
                        return bScorePoints - aScorePoints;
                    });
                }
                setRankingData(data);
            } else {
                setRankingData([]);
            }
            setLoading(false);
        }, (err) => {
            setError('Fout bij het ophalen van de ranglijst.');
            console.error(err);
            setLoading(false);
        });

        return () => {
            unsubscribeRanking();
        };
    }, [auctionId, selectedRound, finalRoundParticipants]);

    // Update default selectedRound based on spelfase - alleen bij spelfase wijziging
    useEffect(() => {
        if (auction?.spelfase === 'Tussenronde' && liveRankingIntermediateRound) {
            setSelectedRound('liveRankingIntermediateRound');
        } else if (auction?.spelfase === 'Finaleronde' && (liveRankingFinaleronde || finalRoundParticipants)) {
            setSelectedRound('liveRankingFinaleronde');
        } else if (auction?.spelfase === 'Tussenronde' && !liveRankingIntermediateRound) {
            if (finalRankingPoule) {
                setSelectedRound('finalRankingPoule');
            }
        } else if (auction?.spelfase && !['Tussenronde', 'Finaleronde'].includes(auction.spelfase)) {
            setSelectedRound('total');
        }
    }, [auction?.spelfase, liveRankingIntermediateRound, finalRankingPoule, liveRankingFinaleronde, finalRoundParticipants]);


    const handleRowClick = (rankingRow) => {
        // Find the full participant object to pass to the modal
        const participant = participants.find(p => p.uid === rankingRow.uid);
        if (participant) {
            setSelectedParticipant(participant);

            // Set the roundId for modal if we're viewing a specific round
            if (selectedRound !== 'total' && selectedRound !== 'finalRankingPoule' && selectedRound !== 'liveRankingIntermediateRound' && selectedRound !== 'liveRankingFinaleronde') {
                // Check in regular rounds first
                const selectedRoundData = closedRounds.find(r => r.id === selectedRound);
                if (selectedRoundData) {
                    setModalRoundId(selectedRoundData.roundId);
                } else {
                    // Check in knockout rounds
                    const knockoutRoundData = knockoutRounds.find(r => r.id === selectedRound);
                    if (knockoutRoundData) {
                        setModalRoundId(knockoutRoundData.roundId || selectedRound);
                    } else {
                        setModalRoundId(null);
                    }
                }
            } else {
                setModalRoundId(null);
            }
        } else {
            // Fallback in case participants haven't loaded yet,
            // or if there's a mismatch. The modal might have less data.
            setSelectedParticipant({ uid: rankingRow.uid, name: rankingRow.name });
        }
    };

    const handleCloseModal = () => {
        setSelectedParticipant(null);
        setModalRoundId(null);
    };

    // Tel speelronde-overwinningen (medailles) en bepaal de winnaars per ronde
    useEffect(() => {
        if (!auctionId || (closedRounds.length === 0 && knockoutRounds.length === 0)) {
            setPouleWinCounts({});
            setRoundWinners([]);
            return;
        }
        let cancelled = false;
        const scoreOf = (r) => (r.playerPoints || 0) + (r.tacticPoints || 0) + (r.tacticCounter || 0);
        const getWinner = (results) => {
            if (!results || results.length === 0) return null;
            return [...results].sort((a, b) => {
                if ((b.matchPoints || 0) !== (a.matchPoints || 0)) {
                    return (b.matchPoints || 0) - (a.matchPoints || 0);
                }
                return scoreOf(b) - scoreOf(a);
            })[0];
        };

        // Geordende lijst: eerst speelrondes 1 t/m 8, daarna tussenronde 1 en 2
        const pouleSorted = [...closedRounds].sort((a, b) =>
            parseInt(a.id.replace('round', ''), 10) - parseInt(b.id.replace('round', ''), 10)
        );
        const rounds = [
            ...pouleSorted.map((r) => ({
                id: r.id,
                type: 'poule',
                label: `Speelronde ${r.id.replace('round', '')}`,
            })),
            ...knockoutRounds.map((r, index) => {
                let roundNumber;
                if (r.leg) {
                    roundNumber = r.leg.split('/')[0];
                } else if (r.id?.startsWith('intermediateRound')) {
                    roundNumber = r.id.replace('intermediateRound', '');
                } else {
                    roundNumber = index + 1;
                }
                return { id: r.id, type: 'tussen', label: `Tussenronde ${roundNumber}` };
            }),
        ];

        const fetchWinners = async () => {
            const winnersById = {};
            await Promise.all(rounds.map(async (r) => {
                const snap = await getDoc(doc(db, 'auctions', auctionId, 'roundRankings', r.id));
                winnersById[r.id] = snap.exists() ? getWinner(snap.data().results || []) : null;
            }));
            if (cancelled) return;

            const counts = {};
            const overview = rounds.map((r) => {
                const winner = winnersById[r.id];
                if (r.type === 'poule' && winner?.uid) {
                    counts[winner.uid] = (counts[winner.uid] || 0) + 1;
                }
                return { id: r.id, label: r.label, name: winner?.name || '—' };
            });
            setPouleWinCounts(counts);
            setRoundWinners(overview);
        };
        fetchWinners();
        return () => { cancelled = true; };
    }, [auctionId, closedRounds, knockoutRounds]);

    // Oud-kampioenen: deelnemers die ooit een seizoen wonnen krijgen een goud bekertje.
    useEffect(() => {
        let cancelled = false;
        fetchGoldMedalUids()
            .then((uids) => { if (!cancelled) setGoldUids(uids); })
            .catch((err) => console.error('Kon gouden bekers niet ophalen:', err));
        return () => { cancelled = true; };
    }, []);

    // Bepaal of de finaleronde is verwerkt (de 'Final' ronde is gesloten)
    const isFinaleProcessed = useMemo(() => {
        const finalRound = finalRoundSchedule.find(round => round.stageName === 'Final');
        return finalRound?.status === 'closed';
    }, [finalRoundSchedule]);

    // Spelersdeelnemingen: transfers met een winstpercentage waarvan het ontvangende
    // team (to) in de eindstand van de finaleronde staat
    const participations = useMemo(() => {
        if (selectedRound !== 'liveRankingFinaleronde' || !isFinaleProcessed) return [];
        const finaleUids = new Set(rankingData.map(row => row.uid));
        const nameOf = (uid) => {
            if (!uid) return '—';
            if (uid === 'vrije-markt') return 'Vrije markt';
            const participant = participants.find(p => p.uid === uid);
            return participant?.name || uid;
        };
        return transfers
            .filter(t => (t.profitPercentage || 0) > 0 && finaleUids.has(t.to))
            .map(t => ({
                id: t.id,
                beneficiaryName: nameOf(t.profitBeneficiary || t.from),
                toName: nameOf(t.to),
                playerName: t.playerName || '—',
                percentage: t.profitPercentage,
            }))
            .sort((a, b) =>
                a.toName.localeCompare(b.toName) ||
                a.beneficiaryName.localeCompare(b.beneficiaryName) ||
                a.playerName.localeCompare(b.playerName)
            );
    }, [transfers, participants, rankingData, selectedRound, isFinaleProcessed]);

    const sortedRounds = useMemo(() => {
        return [...closedRounds].sort((a, b) => {
            const roundNumA = parseInt(a.id.replace('round', ''), 10);
            const roundNumB = parseInt(b.id.replace('round', ''), 10);
            return roundNumA - roundNumB;
        });
    }, [closedRounds]);

    if (loading) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    // In de Voorbereiding en de Veiling vervangt de veiling-competitie de normale ranglijst volledig.
    if (AUCTION_COMPETITION_PHASES.includes(effectivePhase)) {
        return (
            <Box>
                <Box sx={page.sectionHeader}>
                    <Typography variant="h4" gutterBottom component="div" sx={{ mb: 0 }}>Stand</Typography>
                </Box>
                <AuctionCompetitionTable
                    ranking={auctionCompetition}
                    onRowClick={handleRowClick}
                    currentUserId={user?.uid}
                    goldUids={goldUids}
                />
                {selectedParticipant && (
                    <ParticipantTeamModal
                        open={!!selectedParticipant}
                        onClose={handleCloseModal}
                        auctionId={auctionId}
                        participant={selectedParticipant}
                        roundId={null}
                        seasonId={auction?.season}
                        roundStatus={null}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={page.sectionHeader}>
                <Typography variant="h4" gutterBottom component="div" sx={{ mb: 0 }}>Ranglijst</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        id="round-select"
                        value={selectedRound}
                        onChange={(e) => setSelectedRound(e.target.value)}
                    >
                        {!['Tussenronde', 'Finaleronde'].includes(auction?.spelfase) && (
                            <MenuItem value="total">Totaal</MenuItem>
                        )}
                        {liveRankingIntermediateRound && (
                            <MenuItem value="liveRankingIntermediateRound">Totaal tussenronde</MenuItem>
                        )}
                        {(liveRankingFinaleronde || auction?.spelfase === 'Finaleronde') && (
                            <MenuItem value="liveRankingFinaleronde">Totaal finaleronde</MenuItem>
                        )}
                        {knockoutRounds.map((round, index) => {
                            let roundNumber;
                            if (round.leg) {
                                roundNumber = round.leg.split('/')[0];
                            } else if (round.id?.startsWith('intermediateRound')) {
                                roundNumber = round.id.replace('intermediateRound', '');
                            } else {
                                roundNumber = index + 1;
                            }
                            return (
                                <MenuItem key={round.id} value={round.id}>
                                    Tussenronde {roundNumber}
                                </MenuItem>
                            );
                        })}
                        {finalRoundSchedule.map((round) => {
                            const label = round.stageName === 'Final' 
                                ? 'Final' 
                                : `${round.stageName} ${round.leg || ''}`;
                            return (
                                <MenuItem key={round.id} value={round.id}>
                                    {label}
                                </MenuItem>
                            );
                        })}
                        {achtsteFinaleRounds.map((round) => {
                            const legNumber = round.leg ? round.leg.split('/')[0] : '';
                            return (
                                <MenuItem key={round.id} value={round.id}>
                                    Achtste finale {legNumber}
                                </MenuItem>
                            );
                        })}
                        {sortedRounds.map(round => (
                            <MenuItem key={round.id} value={round.id}>
                                Speelronde {round.id.replace('round', '')}
                            </MenuItem>
                        ))}
                        {finalRankingPoule && (
                            <MenuItem value="finalRankingPoule">Eindstand Poulefase</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Box>
            <RankingTable ranking={rankingData} onRowClick={handleRowClick} isMobile={isMobile} currentUserId={user?.uid} showChampion={selectedRound === 'liveRankingFinaleronde' && isFinaleProcessed} showMedals={selectedRound === 'finalRankingPoule'} medalCounts={pouleWinCounts} goldUids={goldUids} />
            {selectedRound === 'liveRankingFinaleronde' && isFinaleProcessed && roundWinners.length > 0 && (
                <Box sx={{ ...containers.glassContainer, mt: 3, p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Winnaars speelrondes &amp; tussenrondes
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 1,
                    }}>
                        {roundWinners.map((w) => (
                            <Box
                                key={w.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    py: 0.75,
                                    px: 1.5,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {w.label}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    🥇 {w.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            {selectedRound === 'liveRankingFinaleronde' && isFinaleProcessed && participations.length > 0 && (
                <Box sx={{ ...containers.glassContainer, mt: 3, p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Spelersdeelnemingen
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                        gap: 1,
                    }}>
                        {participations.map((p) => (
                            <Box
                                key={p.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    py: 0.75,
                                    px: 1.5,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {p.beneficiaryName} <Box component="span" sx={{ color: 'text.disabled' }}>→</Box> {p.toName}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                    {p.playerName} · {p.percentage}%
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            {selectedParticipant && (
                <ParticipantTeamModal
                    open={!!selectedParticipant}
                    onClose={handleCloseModal}
                    auctionId={auctionId}
                    participant={selectedParticipant}
                    roundId={modalRoundId}
                    seasonId={auction?.season}
                    roundStatus={selectedRound !== 'total' ? 'closed' : null}
                />
            )}
        </Box>
    );
}

export default Rankings;
